import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, BookOpen, Image, LogOut, Plus, Edit, Trash2, 
  Eye, EyeOff, Save, X, Upload, Menu, ChevronDown, Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/images/albmove-logo-horizontal.png";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('blog');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Blog state
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);
  
  // Gallery state
  const [images, setImages] = useState([]);
  const [editingImage, setEditingImage] = useState(null);
  const [showImageForm, setShowImageForm] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    verifyToken();
    fetchData();
  }, [token, navigate]);

  const verifyToken = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Invalid token');
    } catch {
      localStorage.removeItem('adminToken');
      navigate('/admin');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchPosts(), fetchImages()]);
    setLoading(false);
  };

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blog`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      } else {
        console.error('Error fetching posts:', res.status);
        setPosts([]);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    }
  };

  const fetchImages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data);
      } else {
        console.error('Error fetching images:', res.status);
        setImages([]);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin');
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      return data.url;
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Blog CRUD
  const savePost = async (postData) => {
    setLoading(true);
    try {
      const url = editingPost 
        ? `${API_URL}/api/admin/blog/${editingPost.id}`
        : `${API_URL}/api/admin/blog`;
      
      const res = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });
      
      if (res.ok) {
        await fetchPosts();
        setShowPostForm(false);
        setEditingPost(null);
      }
    } catch (error) {
      console.error('Error saving post:', error);
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Jeni i sigurt që doni të fshini këtë postim?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/blog/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  // Gallery CRUD
  const saveImage = async (imageData) => {
    setLoading(true);
    try {
      const url = editingImage
        ? `${API_URL}/api/admin/gallery/${editingImage.id}`
        : `${API_URL}/api/admin/gallery`;
      
      const res = await fetch(url, {
        method: editingImage ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(imageData)
      });
      
      if (res.ok) {
        await fetchImages();
        setShowImageForm(false);
        setEditingImage(null);
      }
    } catch (error) {
      console.error('Error saving image:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Jeni i sigurt që doni të fshini këtë imazh?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/gallery/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-slate-900 text-white transition-all duration-300 z-40 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            {sidebarOpen ? (
              <img src={LOGO_URL} alt="AlbMove" className="h-8" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5" />
              </div>
            )}
          </div>
        </div>
        
        <nav className="p-4 space-y-2">
          <button
            onClick={() => setActiveTab('blog')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'blog' ? 'bg-blue-600' : 'hover:bg-slate-800'
            }`}
          >
            <BookOpen className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Blog</span>}
          </button>
          
          <button
            onClick={() => setActiveTab('gallery')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'gallery' ? 'bg-blue-600' : 'hover:bg-slate-800'
            }`}
          >
            <Image className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Galeria</span>}
          </button>
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-600/20 text-red-400 transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Dil</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-slate-900">
                {activeTab === 'blog' ? 'Menaxhimi i Blog-ut' : 'Menaxhimi i Galerisë'}
              </h1>
            </div>
            
            <Button
              onClick={() => activeTab === 'blog' ? setShowPostForm(true) : setShowImageForm(true)}
              className="bg-blue-600 hover:bg-blue-700 rounded-xl"
            >
              <Plus className="w-5 h-5 mr-2" />
              {activeTab === 'blog' ? 'Postim i Ri' : 'Imazh i Ri'}
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {loading && !showPostForm && !showImageForm ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeTab === 'blog' ? (
            <BlogManager
              posts={posts}
              onEdit={(post) => { setEditingPost(post); setShowPostForm(true); }}
              onDelete={deletePost}
            />
          ) : (
            <GalleryManager
              images={images}
              onEdit={(img) => { setEditingImage(img); setShowImageForm(true); }}
              onDelete={deleteImage}
            />
          )}
        </div>
      </main>

      {/* Post Form Modal */}
      {showPostForm && (
        <PostFormModal
          post={editingPost}
          onSave={savePost}
          onClose={() => { setShowPostForm(false); setEditingPost(null); }}
          onUpload={uploadImage}
          uploading={uploading}
        />
      )}

      {/* Image Form Modal */}
      {showImageForm && (
        <ImageFormModal
          image={editingImage}
          onSave={saveImage}
          onClose={() => { setShowImageForm(false); setEditingImage(null); }}
          onUpload={uploadImage}
          uploading={uploading}
        />
      )}
    </div>
  );
};

// Blog Manager Component
const BlogManager = ({ posts, onEdit, onDelete }) => {
  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center">
        <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Nuk ka postime ende</h3>
        <p className="text-slate-500">Krijoni postimin e parë duke klikuar butonin "Postim i Ri"</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Titulli</th>
            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Kategoria</th>
            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Statusi</th>
            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Shikime</th>
            <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Veprime</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {posts.map(post => (
            <tr key={post.id} className="hover:bg-slate-50">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  {post.cover_image && (
                    <img src={post.cover_image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  )}
                  <div>
                    <p className="font-medium text-slate-900 line-clamp-1">{post.title}</p>
                    <p className="text-sm text-slate-500 line-clamp-1">{post.excerpt}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium">
                  {post.category}
                </span>
              </td>
              <td className="px-6 py-4">
                {post.published ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <Eye className="w-4 h-4" /> Publikuar
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-slate-400">
                    <EyeOff className="w-4 h-4" /> Draft
                  </span>
                )}
              </td>
              <td className="px-6 py-4 text-slate-600">{post.views}</td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onEdit(post)}
                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(post.id)}
                    className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Gallery Manager Component
const GalleryManager = ({ images, onEdit, onDelete }) => {
  if (images.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center">
        <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Nuk ka imazhe ende</h3>
        <p className="text-slate-500">Shtoni imazhin e parë duke klikuar butonin "Imazh i Ri"</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {images.map(image => (
        <div key={image.id} className="group relative bg-white rounded-xl overflow-hidden shadow-sm">
          <div className="aspect-square">
            <img src={image.image_url} alt={image.title} className="w-full h-full object-cover" />
          </div>
          
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              onClick={() => onEdit(image)}
              className="p-3 bg-white rounded-full text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={() => onDelete(image.id)}
              className="p-3 bg-white rounded-full text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-3">
            <p className="font-medium text-slate-900 text-sm line-clamp-1">{image.title}</p>
            <p className="text-xs text-slate-500">{image.category}</p>
          </div>
          
          {!image.published && (
            <div className="absolute top-2 right-2 px-2 py-1 bg-yellow-500 text-white text-xs rounded-full">
              Draft
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Post Form Modal
const PostFormModal = ({ post, onSave, onClose, onUpload, uploading }) => {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    cover_image: post?.cover_image || '',
    category: post?.category || 'Lajme',
    tags: post?.tags?.join(', ') || '',
    published: post?.published || false
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await onUpload(file);
      if (url) {
        setFormData(prev => ({ ...prev, cover_image: url }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10">
      <div className="bg-white rounded-2xl w-full max-w-3xl mx-4 my-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {post ? 'Modifiko Postimin' : 'Postim i Ri'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Titulli *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              minLength={5}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              placeholder="Titulli i postimit"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Përmbledhje *</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              required
              minLength={10}
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none resize-none"
              placeholder="Një përmbledhje e shkurtër e postimit"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Përmbajtja *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              required
              minLength={50}
              rows={10}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none resize-none"
              placeholder="Shkruani përmbajtjen e plotë të postimit..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Imazhi i Kopertinës</label>
            <div className="flex items-center gap-4">
              {formData.cover_image && (
                <img src={formData.cover_image} alt="Cover" className="w-24 h-24 rounded-lg object-cover" />
              )}
              <label className="flex items-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl cursor-pointer transition-colors">
                <Upload className="w-5 h-5" />
                {uploading ? 'Duke ngarkuar...' : 'Ngarko Imazh'}
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Kategoria</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              >
                <option value="Lajme">Lajme</option>
                <option value="Teknologji">Teknologji</option>
                <option value="Këshilla">Këshilla</option>
                <option value="Projekte">Projekte</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Etiketa (të ndara me presje)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                placeholder="EV, Karikim, Teknologji"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
              className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="published" className="text-sm font-medium text-slate-700">
              Publiko menjëherë
            </label>
          </div>
          
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Anulo
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              <Save className="w-5 h-5 mr-2" />
              Ruaj
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Image Form Modal
const ImageFormModal = ({ image, onSave, onClose, onUpload, uploading }) => {
  const [formData, setFormData] = useState({
    title: image?.title || '',
    description: image?.description || '',
    image_url: image?.image_url || '',
    category: image?.category || 'Stacione',
    order: image?.order || 0,
    published: image?.published ?? true
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await onUpload(file);
      if (url) {
        setFormData(prev => ({ ...prev, image_url: url }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {image ? 'Modifiko Imazhin' : 'Imazh i Ri'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Imazhi *</label>
            <div className="flex flex-col items-center gap-4">
              {formData.image_url ? (
                <img src={formData.image_url} alt="Preview" className="w-full h-48 rounded-xl object-cover" />
              ) : (
                <div className="w-full h-48 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Image className="w-12 h-12 text-slate-300" />
                </div>
              )}
              <label className="flex items-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl cursor-pointer transition-colors">
                <Upload className="w-5 h-5" />
                {uploading ? 'Duke ngarkuar...' : 'Ngarko Imazh'}
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" required={!formData.image_url} />
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Titulli *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              placeholder="Titulli i imazhit"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Përshkrimi</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none resize-none"
              placeholder="Përshkrim i shkurtër (opsional)"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Kategoria</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              >
                <option value="Stacione">Stacione</option>
                <option value="Projekte">Projekte</option>
                <option value="Evente">Evente</option>
                <option value="Ekipi">Ekipi</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Rendi</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="img-published"
              checked={formData.published}
              onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
              className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="img-published" className="text-sm font-medium text-slate-700">
              Publiko
            </label>
          </div>
          
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Anulo
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              <Save className="w-5 h-5 mr-2" />
              Ruaj
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
